import classNames from 'classnames';
import { useState } from 'react';
import Button from '@/shared/components/shared/Button';
import Link from '@/shared/components/shared/Link';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { tp } from '@/shared/lib/typograf';
import { scrollToAnchor } from '@/shared/lib/utils/scrollToAnchor';

type FormatsSectionItem = {
    id: string;
    title?: string | null;
    text?: string | null;
    href?: string | null;
};

type FormatsSectionTab = {
    id: string;
    label?: string | null;
    activeItemIds?: string[] | null;
};

export type FormatsSectionRawProps = {
    title?: string | null;
    text?: string | null;
    buttonText?: string | null;
    buttonHref?: string | null;
    tabsLabel?: string | null;
    tabs?: FormatsSectionTab[] | null;
    items?: FormatsSectionItem[] | null;
};

type Props = Omit<React.HTMLAttributes<HTMLElement>, 'title'> & FormatsSectionRawProps;

function FormatsSectionCard({
    title,
    text,
    href,
    index,
    isActive,
}: FormatsSectionItem & { index: number; isActive: boolean }) {
    const tpTitle = useTypograf(title ?? '');
    const tpText = useTypograf(text ?? '');
    const number = String(index + 1).padStart(2, '0');

    if (!tpTitle && !tpText) {
        return null;
    }

    const cardInner = (
        <>
            <div className="formats-section__card-number text-xl" aria-hidden="true">
                {number}
            </div>
            {tpTitle && (
                <div
                    className={classNames('formats-section__card-title text-l', {
                        link: Boolean(href),
                        'link--underlined': Boolean(href),
                    })}
                >
                    {tpTitle}
                </div>
            )}
            {tpText && <div className="formats-section__card-text text-s">{tpText}</div>}
        </>
    );

    return (
        <li
            className={classNames('formats-section__item', {
                'formats-section__item--active': isActive,
            })}
        >
            {href ? (
                <Link href={href} className="formats-section__card-link">
                    {cardInner}
                </Link>
            ) : (
                cardInner
            )}
        </li>
    );
}

function FormatsSection({ title, text, buttonText, buttonHref, tabsLabel, tabs, items, className, ...props }: Props) {
    const filteredTabs = tabs?.filter((tab) => tab.id && tab.label) ?? [];
    const filteredItems = items?.filter((item) => item.id && (item.title || item.text)) ?? [];
    const [activeTabId, setActiveTabId] = useState(filteredTabs[0]?.id ?? '');
    const tpTitle = useTypograf(title ?? '');
    const tpText = useTypograf(text ?? '');
    const tpButtonText = useTypograf(buttonText ?? '');
    const tpTabsLabel = useTypograf(tabsLabel ?? '');
    const activeTab = filteredTabs.find((tab) => tab.id === activeTabId) ?? filteredTabs[0];
    const activeItemIds = activeTab?.activeItemIds ?? [];
    const hasHeader = Boolean(tpTitle || tpText || tpButtonText);
    const hasPanel = Boolean(tpTabsLabel || filteredTabs.length > 0 || filteredItems.length > 0);
    const resolvedButtonHref = buttonHref ?? '#participantsList';
    const isAnchorHref = resolvedButtonHref.startsWith('#');
    const buttonAnchorId = isAnchorHref ? resolvedButtonHref.slice(1) : null;

    const handleAnchorClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!buttonAnchorId) {
            return;
        }

        event.preventDefault();
        scrollToAnchor(buttonAnchorId);
    };

    if (!hasHeader && !hasPanel) {
        return null;
    }

    return (
        <section
            {...props}
            className={classNames('formats-section js-header-theme-trigger', className)}
            data-theme="dark"
        >
            <div className="wrapper formats-section__wrapper">
                {hasHeader && (
                    <div className="formats-section__header">
                        {tpTitle && (
                            <div className="formats-section__title-wrapper">
                                <h2 className="formats-section__title h3">{tpTitle}</h2>
                            </div>
                        )}
                        {tpText && (
                            <div
                                className="formats-section__text wysiwyg text-s"
                                dangerouslySetInnerHTML={{ __html: tpText }}
                            />
                        )}
                        {tpButtonText && (
                            <Button
                                className="formats-section__btn"
                                tag="a"
                                href={resolvedButtonHref}
                                variant="outline-dark"
                                onClick={handleAnchorClick}
                            >
                                {tpButtonText}
                            </Button>
                        )}
                    </div>
                )}

                {hasPanel && (
                    <div className="formats-section__panel">
                        {(tpTabsLabel || filteredTabs.length > 0) && (
                            <div className="formats-section__panel-top">
                                {tpTabsLabel && <div className="formats-section__tabs-label text-m">{tpTabsLabel}</div>}
                                {filteredTabs.length > 0 && (
                                    <div className="formats-section__tabs" role="tablist">
                                        {filteredTabs.map((tab) => (
                                            <Button
                                                key={tab.id}
                                                type="button"
                                                role="tab"
                                                aria-selected={tab.id === activeTab?.id}
                                                className={classNames('formats-section__tab', {
                                                    active: tab.id === activeTab?.id,
                                                })}
                                                variant="grey-rounded-checkable"
                                                onClick={() => setActiveTabId(tab.id)}
                                            >
                                                {tp(tab.label ?? '')}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {filteredItems.length > 0 && (
                            <ul className="formats-section__list list-unstyled">
                                {filteredItems.map((item, index) => (
                                    <FormatsSectionCard
                                        key={item.id}
                                        {...item}
                                        index={index}
                                        isActive={activeItemIds.includes(item.id)}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

export default FormatsSection;
